import type {
  DiagnosticsLogLevelV2,
  DeviceDiagnosticLogChannel,
  DeviceDiagnosticLogContext,
  CompletedSaleReportSourceTypeV4,
  CreateCompletedSaleReportsV4Request,
} from '@am3nd-org/pointcraft-types';

export type SystemConfigType<T> = {
  id?: number;
  type: string;
  data: T;
};

export type InventoryStats = {
  id: string;
  totalItems: number;
  totalActiveItems: number;
  totalDeletedItems: number;
  totalStockQuantity: number;
  categoryCounts: Array<{ category: string; count: number }>;
  lastCalculatedAt: string;
  inventoryVersion?: number;
};

export type RestaurantOrderEnrichmentCacheRecord<T = unknown> = {
  cacheKey: string;
  businessId: string;
  saleId: string;
  payload: T;
  sourceUpdatedAt: string | null;
  fetchedAt: string;
  expiresAt: string;
};

export type CompletedSaleReportHandoffState = 'PENDING_API' | 'PENDING_FIRESTORE';

export const COMPLETED_SALE_REPORT_HISTORY_LIMIT = 100;

export type CompletedSaleReportResolution = 'API_ACCEPTED' | 'FIRESTORE_RECOVERY_CREATED';

/**
 * Durable device-local journal for completed POS/terminal payment truth.
 * The request is immutable after insertion. A record exists only while delivery is pending and is
 * deleted as soon as either the API accepts it or Firestore recovery takes ownership.
 */
export type CompletedSaleReportOutboxRecord = {
  version: 4;
  reportIdempotencyKey: string;
  businessId: string;
  saleId: string;
  transactionId: string;
  sourceType: CompletedSaleReportSourceTypeV4;
  sourceId: string;
  terminalId: string | null;
  request: CreateCompletedSaleReportsV4Request;
  state: CompletedSaleReportHandoffState;
  apiAttemptCount: number;
  createdAt: string;
  updatedAt: string;
  lastAttemptAt: string | null;
  lastError: string | null;
  /** Reserved null column for compatibility with the existing SQLite table layout. */
  handedOffAt: string | null;
  /** Durable delivery/recovery eligibility; avoids hot retries across restarts. */
  nextHandoffAttemptAt: string | null;
  /** Consecutive Firestore handoff failures used for bounded backoff. */
  recoveryFailureCount: number;
};

/**
 * Bounded, device-local audit record created after completed-report ownership transfers.
 * The immutable request is retained for support inspection, but no credentials, raw provider
 * payloads, headers, or response bodies are added beyond the existing receipt-safe contract.
 */
export type CompletedSaleReportHistoryRecord = {
  version: 1;
  reportIdempotencyKey: string;
  businessId: string;
  saleId: string;
  transactionId: string;
  sourceType: CompletedSaleReportSourceTypeV4;
  sourceId: string;
  terminalId: string | null;
  request: CreateCompletedSaleReportsV4Request;
  resolution: CompletedSaleReportResolution;
  apiAcceptanceStatus: 'ACCEPTED' | 'DEDUPLICATED' | null;
  reportReference: string | null;
  remoteAcceptedAt: string | null;
  recoveryDocumentId: string | null;
  apiAttemptCount: number;
  recoveryFailureCount: number;
  lastError: string | null;
  createdAt: string;
  completedAt: string;
};

export type OfflineMutationState = 'PENDING' | 'REJECTED';

export type OfflineMutationOperation = 'CURRENT_DEVICE_TERMINAL_ASSIGNMENT';

export type OfflineMutationErrorDetails = {
  kind: string;
  code: string | null;
  httpStatus: number | null;
  message: string;
  occurredAt: string;
};

export type CurrentDeviceTerminalAssignmentMutationPayload = {
  request: {
    businessDeviceId: string;
    name: string;
    terminalId: string;
  };
  businessId: string;
  businessDeviceId: string;
  businessLocationId: string;
  previousTerminalId: string | null;
  selectedTerminalId: string;
};

type OfflineMutationRecordBase = {
  /** Monotonic, storage-assigned FIFO position. */
  sequence: number;
  mutationId: string;
  schemaVersion: 1;
  state: OfflineMutationState;
  businessId: string;
  businessDeviceId: string;
  orderingKey: string;
  /** The API operation reached a terminal outcome and still needs authoritative reconciliation. */
  reconciliationPending: boolean;
  attemptCount: number;
  createdAt: string;
  updatedAt: string;
  lastAttemptAt: string | null;
  nextAttemptAt: string | null;
  lastError: OfflineMutationErrorDetails | null;
};

/**
 * Durable, typed API mutation journal. Each operation must be explicitly registered and
 * idempotent/replay-safe; arbitrary URLs, headers, credentials, and authentication tokens are
 * deliberately not representable.
 */
export type OfflineMutationRecord = OfflineMutationRecordBase & {
  operation: 'CURRENT_DEVICE_TERMINAL_ASSIGNMENT';
  payload: CurrentDeviceTerminalAssignmentMutationPayload;
};

export type NewOfflineMutationRecord = Omit<OfflineMutationRecord, 'sequence'>;

// ---------------------------------------------------------------------------
// diagnostic_log_records — device diagnostic logging (see src/features/diagnostics)
// ---------------------------------------------------------------------------

/** Only PENDING rows exist today; uploaded rows are deleted, not marked. */
export type DiagnosticLogRecordState = 'PENDING';

/** The variable-size half of a record, stored as JSON. */
export type DiagnosticLogRecordPayload = {
  message: string;
  /** Immutable capture scope; absent on incompatible legacy diagnostic rows. */
  capture?: {
    businessId: string | null;
    businessDeviceId: string | null;
    sessionId: string;
    appVersion: string;
    platform: string;
    siteId: string;
    environment: string;
  };
  sequence?: number;
  data?: unknown;
  ctx?: DeviceDiagnosticLogContext;
};

/**
 * One durable diagnostic record. Columns that are indexed or needed to build
 * an upload envelope are stored as columns; everything else lives in
 * `payload`. `sizeBytes` is measured once at capture so the storage cap can
 * be enforced with a SUM instead of a scan.
 */
export type DiagnosticLogRecordRow = {
  /** Monotonic, storage-assigned FIFO position. */
  seq: number;
  recordId: string;
  businessId: string;
  sessionId: string;
  occurredAt: string;
  level: DiagnosticsLogLevelV2;
  channel: DeviceDiagnosticLogChannel;
  state: DiagnosticLogRecordState;
  sizeBytes: number;
  payload: DiagnosticLogRecordPayload;
};

export type NewDiagnosticLogRecordRow = Omit<DiagnosticLogRecordRow, 'seq'>;

/** Read-only cursor over stored records, newest storage sequence first. */
export type DiagnosticLogPageQuery = {
  businessId: string;
  beforeSequence?: number;
  limit: number;
  level?: DiagnosticsLogLevelV2;
  channel?: DeviceDiagnosticLogChannel;
};

export type DiagnosticLogStorageStats = {
  count: number;
  totalBytes: number;
  oldestOccurredAt: string | null;
  newestOccurredAt: string | null;
  /** Business of the newest row; used to detect a re-paired device at boot. */
  newestBusinessId: string | null;
};
